import { getCachedReauthToken, requestReauth } from '../hooks/useReauth';
import { readPersistedValue, writePersistedValue } from './sessionPersistence';

export function resolveApiBaseUrl(): string {
  // Explicit env config takes precedence (production behind nginx reverse proxy)
  const envConfig = import.meta.env?.VITE_API_BASE_URL;
  if (typeof envConfig === 'string' && envConfig.trim().length > 0) {
    return envConfig.replace(/\/$/, '');
  }

  // For mobile/network access on a local IP: dynamically use same hostname with API port
  // Only kicks in when accessing via a raw IPv4 address (e.g., 192.168.x.x).
  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;
    const apiPort = import.meta.env?.VITE_API_PORT || '4000';
    const isIpv4 = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname);

    if (isIpv4) {
      return `${protocol}//${hostname}:${apiPort}`;
    }
  }

  const globalConfig = (globalThis as unknown as { __PROP_VISION_API_BASE_URL?: unknown }).__PROP_VISION_API_BASE_URL;

  const candidates = [
    typeof globalConfig === 'string' ? globalConfig : undefined,
    typeof envConfig === 'string' ? envConfig : undefined,
    typeof process !== 'undefined' ? process.env?.EXPO_PUBLIC_API_BASE_URL : undefined,
    typeof process !== 'undefined' ? process.env?.API_BASE_URL : undefined,
    typeof window !== 'undefined' ? window.location.origin : undefined,
  ];

  const baseUrl = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);

  if (!baseUrl) {
    console.warn('Missing API base URL configuration. Falling back to relative API requests.');
    return '';
  }

  return baseUrl.replace(/\/$/, '');
}

const apiBase = resolveApiBaseUrl();

// ---------------------------------------------------------------------------
// Access-token refresh. On a 401 for an authenticated request we exchange the
// stored refresh token for a fresh access token (rotated server-side) and retry
// the request once. Concurrent 401s share a single in-flight refresh.
// localStorage is the source of truth for the refresh token; a registered
// callback keeps React state (useAuth) in sync so later requests use the new
// token directly instead of 401-ing again.
// ---------------------------------------------------------------------------

// Must match SESSION_STORAGE_KEY in hooks/useAuth.tsx.
const AUTH_SESSION_KEY = 'safaqat24.auth.session';

interface RefreshableSession {
  token: string;
  refreshToken?: string;
  expiresAt?: string;
  user: unknown;
}

let onSessionRefreshed: ((session: RefreshableSession) => void) | null = null;
let onSessionCleared: (() => void) | null = null;

/** Registered by AuthProvider so refreshes/clears propagate into React state. */
export function registerSessionSync(handlers: {
  onRefreshed: (session: RefreshableSession) => void;
  onCleared: () => void;
}): void {
  onSessionRefreshed = handlers.onRefreshed;
  onSessionCleared = handlers.onCleared;
}

let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = (async () => {
    try {
      const stored = await readPersistedValue<RefreshableSession>(AUTH_SESSION_KEY);
      const refreshToken = stored?.refreshToken;
      if (!refreshToken) {
        return null;
      }

      const response = await fetch(`${apiBase}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        // Expired / revoked / reused → drop the session and force a fresh login.
        await writePersistedValue(AUTH_SESSION_KEY, null);
        onSessionCleared?.();
        return null;
      }

      const next = (await response.json()) as RefreshableSession;
      if (!next?.token) {
        return null;
      }
      await writePersistedValue(AUTH_SESSION_KEY, next);
      onSessionRefreshed?.(next);
      return next.token;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export class ApiError extends Error {
  status: number;
  details?: unknown;
  cause?: unknown;

  constructor(message: string, status: number, details?: unknown, cause?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
    this.cause = cause;
  }
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  token?: string | null;
  signal?: AbortSignal;
  retries?: number;
  /** Internal — set to true on the reauth-retry pass to prevent infinite loops. */
  _reauthRetried?: boolean;
  /** Internal — set to true on the token-refresh-retry pass to prevent loops. */
  _refreshRetried?: boolean;
}

const DEFAULT_RETRIES = 2;
const RETRY_DELAY_MS = 500;

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function parseErrorBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    try {
      return await response.json();
    } catch (error) {
      console.warn('Failed to parse JSON error response', error);
      return null;
    }
  }

  try {
    const text = await response.text();
    return text ? { message: text } : null;
  } catch (error) {
    console.warn('Failed to read error response', error);
    return null;
  }
}

function deriveErrorMessage(status: number, _url: string, details: unknown): string {
  if (details && typeof details === 'object' && 'message' in details) {
    const message = String((details as { message?: unknown }).message ?? '').trim();
    if (message) {
      return message;
    }
  }

  if (status === 0) {
    return 'NETWORK_ERROR';
  }

  if (status === 401 || status === 403) {
    return 'INVALID_CREDENTIALS';
  }

  if (status === 429) {
    return 'RATE_LIMIT';
  }

  return 'SERVER_ERROR';
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, token, signal, retries = DEFAULT_RETRIES, _reauthRetried = false, _refreshRetried = false } = options;
  const url = `${apiBase}${path.startsWith('/') ? path : `/${path}`}`;

  const reauthToken = getCachedReauthToken();
  const finalHeaders: Record<string, string> = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(reauthToken ? { 'X-Reauth-Token': reauthToken } : {}),
    ...headers,
  };

  const fetchOptions: RequestInit = {
    method,
    headers: finalHeaders,
    signal,
  };

  if (body instanceof FormData) {
    fetchOptions.body = body;
  } else if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
    fetchOptions.body = JSON.stringify(body);
  }

  let response: Response;
  let lastError: unknown;

  // Retry logic for network failures
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      response = await fetch(url, fetchOptions);
      // If we get a response (even error status), break out of retry loop
      break;
    } catch (error) {
      lastError = error;

      // Don't retry if request was aborted
      if (signal?.aborted) {
        throw new ApiError(
          'Request was cancelled.',
          0,
          undefined,
          error
        );
      }

      // If we have retries left, wait and try again
      if (attempt < retries) {
        const delay = RETRY_DELAY_MS * Math.pow(2, attempt); // Exponential backoff
        console.warn(`API request failed, retrying in ${delay}ms (attempt ${attempt + 1}/${retries})...`);
        await sleep(delay);
        continue;
      }

      // No more retries, throw error
      throw new ApiError(
        'Unable to connect to the server. Please check your internet connection and try again.',
        0,
        undefined,
        error
      );
    }
  }

  // TypeScript guard - response should be defined if we reach here
  if (!response!) {
    throw new ApiError(
      'Unable to connect to the server. Please check your internet connection and try again.',
      0,
      undefined,
      lastError
    );
  }

  // 403 REAUTH_REQUIRED interception — prompt the user once, then retry.
  if (response.status === 403 && !_reauthRetried) {
    const cloneJson = await response.clone().json().catch(() => ({})) as { code?: string };
    if (cloneJson.code === 'REAUTH_REQUIRED') {
      const freshToken = await requestReauth();
      if (freshToken) {
        return apiRequest<T>(path, { ...options, _reauthRetried: true });
      }
      // User cancelled — fall through to throw the 403 as normal
    }
  }

  // 428 TOTP_ENROLLMENT_REQUIRED interception — hard-redirect to enrollment page.
  if (response.status === 428) {
    const body = await response.clone().json().catch(() => ({})) as { code?: string };
    if (body.code === 'TOTP_ENROLLMENT_REQUIRED') {
      // Hard-redirect to the enrollment page. We can't use react-router here
      // because apiClient runs outside React tree.
      if (typeof window !== 'undefined' && window.location.pathname !== '/admin/setup-2fa') {
        window.location.href = '/admin/setup-2fa';
      }
      // Throw a typed error so callers don't try to keep going.
      throw new ApiError('TOTP enrollment required', 428, { code: 'TOTP_ENROLLMENT_REQUIRED' });
    }
  }

  // 401 → try a one-time refresh of the access token, then retry the request.
  // Skipped for the auth endpoints themselves and for unauthenticated requests.
  if (
    response.status === 401 &&
    !_refreshRetried &&
    Boolean(token) &&
    !path.includes('/auth/refresh') &&
    !path.includes('/auth/login')
  ) {
    const refreshedToken = await refreshAccessToken();
    if (refreshedToken) {
      return apiRequest<T>(path, { ...options, token: refreshedToken, _refreshRetried: true });
    }
  }

  if (!response.ok) {
    const details = await parseErrorBody(response);
    const message = deriveErrorMessage(response.status, url, details);
    throw new ApiError(message, response.status, details ?? undefined);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return (await response.json()) as T;
  }

  return (await response.text()) as T;
}
